/**
 * CarouselScene — Three.js version of the showcase roundabout, laid out with the same maths as
 * ProductShowcase's ring, rendered on a transparent canvas so it can be exported as print PNGs.
 */
import * as THREE from 'three';
import { CARD_W, CARD_H, SHADOW_PAD, shadowTexture } from './cardTextures';

const deg = (d) => (d * Math.PI) / 180;

export const baseRadius = () => 560; // the live carousel's 10-card ring radius — "Spread" multiplies this

// Which angle each card sits at: the first `front` cards along the front arc, the rest tucked behind
export const thetaOf = (idx, n, p) => {
  const front = Math.min(Math.max(1, p.front), n);
  if (idx < front) return p.rot + idx * p.step;
  const back = n - front;
  return 180 + (idx - front - (back - 1) / 2) * Math.min(p.step * 1.6, 360 / Math.max(back, 1));
};

export class CarouselScene {
  constructor(canvas) {
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(40, 1, 10, 30000);
    this.ring = new THREE.Group();
    this.scene.add(this.ring);
    this.cards = new Map();
    this.shadowTex = shadowTexture(this.renderer);
    this.cardGeo = new THREE.PlaneGeometry(CARD_W, CARD_H);
    this.shadowGeo = new THREE.PlaneGeometry(CARD_W + SHADOW_PAD * 2, CARD_H + SHADOW_PAD * 2);
  }

  setCards(textures) {
    // textures: Map key -> THREE.Texture. Create/remove meshes to match.
    [...this.cards.keys()].forEach((k) => {
      if (!textures.has(k)) { this.ring.remove(this.cards.get(k).mesh); this.cards.delete(k); }
    });
    textures.forEach((tex, k) => {
      if (this.cards.has(k)) { this.cards.get(k).mesh.material.map = tex; this.cards.get(k).mesh.material.needsUpdate = true; return; }
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, toneMapped: false });
      const mesh = new THREE.Mesh(this.cardGeo, mat);
      const smat = new THREE.MeshBasicMaterial({ map: this.shadowTex, transparent: true, depthWrite: false, opacity: 0.45, toneMapped: false, color: 0x000000 });
      const shadow = new THREE.Mesh(this.shadowGeo, smat);
      shadow.position.set(0, -22, -3);
      mesh.add(shadow);
      this.ring.add(mesh);
      this.cards.set(k, { mesh, shadow });
    });
  }

  layout(order, p) {
    const n = order.length;
    const R = baseRadius() * p.rad;
    this.ring.rotation.x = deg(p.tilt);
    order.forEach((key, idx) => {
      const c = this.cards.get(key);
      if (!c) return;
      const theta = ((thetaOf(idx, n, p) % 360) + 360) % 360;
      const signed = theta > 180 ? theta - 360 : theta;
      const rad = deg(signed);
      const depth = (Math.cos(rad) + 1) / 2;
      c.mesh.visible = true;
      c.mesh.position.set(p.ring * R * Math.sin(rad), 0, p.ring * R * Math.cos(rad) - R);
      c.mesh.rotation.y = deg(signed * p.face);
      c.mesh.scale.setScalar(p.ring);
      const b = p.dim + (1 - p.dim) * depth;
      c.mesh.material.color.setScalar(b);
      c.shadow.material.opacity = 0.45 * p.shadow;
      c.shadow.visible = p.shadow > 0;
    });
    this.cards.forEach((c, key) => { if (!order.includes(key)) c.mesh.visible = false; });
    this.persp = p.persp;
  }

  render(w, h, pixelRatio = 1, background = null) {
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(w, h, false);
    this.camera.clearViewOffset();
    this._aimCamera(w, h);
    if (background) this.renderer.setClearColor(new THREE.Color(background), 1);
    else this.renderer.setClearColor(0x000000, 0);
    this.renderer.render(this.scene, this.camera);
  }

  _aimCamera(w, h) {
    this.camera.aspect = w / h;
    this.camera.fov = (2 * Math.atan(h / 2 / this.persp) * 180) / Math.PI;
    this.camera.position.set(0, 0, this.persp);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  // Tiled export: GPUs silently shrink huge drawing buffers (which cropped/zoomed exports), so render
  // ≤2048px tiles via camera view offsets and stitch them on a 2D canvas.
  async exportPNG(w, h, scale, background = null, onProgress) {
    const fullW = Math.round(w * scale);
    const fullH = Math.round(h * scale);
    if (fullW * fullH > 80e6) throw new Error('Too large — lower the scale or canvas size');
    const out = document.createElement('canvas');
    out.width = fullW; out.height = fullH;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Could not allocate the export canvas — try a lower scale');
    if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, fullW, fullH); }
    const gl = this.renderer.getContext();
    const TILE = Math.min(2048, gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) / 2);
    this.renderer.setPixelRatio(1);
    this.renderer.setClearColor(0x000000, 0);
    this._aimCamera(w, h);
    const tiles = Math.ceil(fullW / TILE) * Math.ceil(fullH / TILE);
    let done = 0;
    for (let y = 0; y < fullH; y += TILE) {
      for (let x = 0; x < fullW; x += TILE) {
        const tw = Math.min(TILE, fullW - x);
        const th = Math.min(TILE, fullH - y);
        this.renderer.setSize(tw, th, false);
        if (gl.drawingBufferWidth !== tw || gl.drawingBufferHeight !== th) throw new Error('Graphics buffer too small for this tile — try a lower scale');
        this.camera.setViewOffset(fullW, fullH, x, y, tw, th);
        this.camera.updateProjectionMatrix();
        this.renderer.render(this.scene, this.camera);
        ctx.drawImage(this.renderer.domElement, 0, 0, tw, th, x, y, tw, th);
        done += 1;
        if (onProgress) onProgress(done / tiles);
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    this.camera.clearViewOffset();
    const blob = await new Promise((resolve) => out.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('PNG encode failed — try a lower scale');
    return blob;
  }

  dispose() {
    this.cards.forEach((c) => { c.mesh.material.dispose(); c.shadow.material.dispose(); });
    this.cardGeo.dispose(); this.shadowGeo.dispose(); this.shadowTex.dispose();
    this.renderer.dispose();
  }
}
